/**
 * poiExtent.ts — viewport-adaptive POI fetch extent (T-245 / BEAD-0088)
 *
 * The POI layer used to fetch a FIXED radius around a point (a constant 1500 m at the only
 * zoom band that fetched) and then REPLACE the rendered set on every debounced `moveend`.
 * Two consequences, both reported by users:
 *
 *   - a slight pan dropped markers that were still on screen — the refetch returned the
 *     N *nearest to the new centre*, and `setData` replaced everything not in that set;
 *   - the fetched area never matched the visible map — nothing in the path read the map
 *     bounds or the screen size, so a wide monitor showed empty edges.
 *
 * This module owns the extent decision as pure functions so it is testable without a browser:
 *
 *   padBbox            the fetch box = the VISIBLE bounds + a buffer (so it follows the viewport)
 *   shouldFetch        skip the request entirely while the viewport stays inside the fetched box
 *   withinAreaGate     bound density by GROUND SIZE, not zoom (zoom alone is screen-size blind —
 *                      which is the very bug; a 4K monitor at z15 sees far more map than a laptop)
 *   mergePois/prunePois  accumulate by id and prune to the fetch box, so a refetch can never
 *                      drop a POI that is still on screen
 *
 * The ALS query itself (centre + circumscribing radius + BoundingBox filter) is built
 * server-side in routes/poi.ts — this module only decides WHAT extent to ask for.
 */

/** [west, south, east, north] — the order Amazon Location Service's BoundingBox uses. */
export type Bbox = [number, number, number, number]

export interface POI {
  id: number
  category: string
  lat: number
  lng: number
  name?: string
  address?: string
}

/**
 * Buffer added around the visible bounds. Fetching a little beyond the screen is what lets a
 * small pan render from the store instead of hitting the network — the buffer IS the headroom
 * that `shouldFetch` spends.
 */
export const POI_BBOX_BUFFER_PCT = 0.3

/**
 * Density bound: refuse to fetch when the padded box's diagonal exceeds this ground distance.
 * "Fetch the whole viewport" at national zoom is the failure mode on the other side of this
 * card, and a nearest-first upstream would only spread a capped result set thinner. ~40 km is a
 * metro-scale box — comfortably more than a full screen at the zooms POIs are useful at, and far
 * below a state/national view.
 */
export const POI_MAX_BBOX_DIAGONAL_M = 40_000

const EARTH_RADIUS_M = 6_371_008.8

function clampLat(lat: number): number {
  return Math.min(90, Math.max(-90, lat))
}

/** Great-circle distance between two lng/lat points, in metres. */
function haversineMeters(lng1: number, lat1: number, lng2: number, lat2: number): number {
  const toRad = (d: number): number => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)))
}

/**
 * Grow a bbox by `pct` of its own width/height on every side. The result therefore scales with
 * the viewport: a wide screen produces a wide fetch box, which is the whole point of the card.
 */
export function padBbox(bbox: Bbox, pct: number = POI_BBOX_BUFFER_PCT): Bbox {
  const [w, s, e, n] = bbox
  const padX = (e - w) * pct
  const padY = (n - s) * pct
  return [w - padX, clampLat(s - padY), e + padX, clampLat(n + padY)]
}

/** True when `inner` lies wholly within `outer` (inclusive — an identical box is contained). */
export function bboxContains(outer: Bbox, inner: Bbox): boolean {
  return (
    inner[0] >= outer[0] && inner[1] >= outer[1] && inner[2] <= outer[2] && inner[3] <= outer[3]
  )
}

/** Ground distance across the box's diagonal, in metres. */
export function bboxDiagonalMeters(bbox: Bbox): number {
  const [w, s, e, n] = bbox
  return haversineMeters(w, s, e, n)
}

/** A box is fetchable only while its ground size stays under the density bound. */
export function withinAreaGate(
  bbox: Bbox,
  maxDiagonalM: number = POI_MAX_BBOX_DIAGONAL_M,
): boolean {
  return bboxDiagonalMeters(bbox) <= maxDiagonalM
}

/**
 * The fetched-extent skip. While the visible box stays inside what we already fetched there is
 * nothing new to ask for — so a small pan issues ZERO requests and, because nothing is replaced,
 * cannot drop a marker.
 */
export function shouldFetch({
  visible,
  lastFetched,
}: {
  visible: Bbox
  lastFetched: Bbox | null
}): boolean {
  if (!lastFetched) return true
  return !bboxContains(lastFetched, visible)
}

/** Union new results into the store, keyed by id. Never drops what is already there. */
export function mergePois(
  existing: ReadonlyMap<number, POI>,
  incoming: readonly POI[],
): Map<number, POI> {
  const next = new Map(existing)
  for (const p of incoming) next.set(p.id, p)
  return next
}

/** Drop stored POIs outside `bbox`, so accumulation stays bounded as the user roams. */
export function prunePois(store: ReadonlyMap<number, POI>, bbox: Bbox): Map<number, POI> {
  const [w, s, e, n] = bbox
  const next = new Map<number, POI>()
  for (const [id, p] of store) {
    if (p.lng >= w && p.lng <= e && p.lat >= s && p.lat <= n) next.set(id, p)
  }
  return next
}
